<template>
  <div class="flex items-start mt-4 space-x-2">
    <div class="flex-grow" ref="conditionEl">
      <div
        class="flex flex-wrap items-stretch gap-x-2 gap-y-3"
        :key="integratedPartner">
        <div
          v-if="isDisconnectedSource"
          class="flex-grow flex items-start gap-2 px-3 py-2 border border-red-400 rounded bg-red-50 text-sm text-red-700">
          <ExclamationTriangleIcon class="w-5 h-5 flex-shrink-0 mt-0.5" />
          <span>
            This rule references <strong>{{ disconnectedSourceLabel }}</strong>, which isn't a connected integration.
            It's safely ignored at runtime — no visitors will match it, and your other rules still work normally.
            Remove it with the <strong>X</strong>, or reconnect {{ disconnectedSourceLabel }} to use it again.
          </span>
        </div>

        <CascadeSelect
          v-else
          v-model="combinedSourceType"
          :options="resolvedSourceFields"
          :focus-on-hover="true"
          optionGroupLabel="group"
          optionGroupChildren="options"
          optionLabel="label"
          optionValue="value"
          placeholder="Select A Trigger">
          <template #value="{ value, placeholder }">
            <div v-if="!value" class="text-sm">
              {{ placeholder }}
            </div>
            <div v-else class="flex items-center gap-2">
              <div class="text-xs opacity-70">{{ sourceLabel }}</div>
              <div class="text-sm">{{ sourceValue }}</div>
            </div>
          </template>
        </CascadeSelect>

        <template v-if="!isDisconnectedSource" v-for="field in availableFields" :key="field.key">
          <Select
            v-if="field.$formkit === 'select'"
            v-model="localCondition[field.name]"
            :options="typeof field.options === 'function' ? field.options(localCondition) : field.options"
            optionLabel="label"
            optionValue="value"
            :autoOptionFocus="true" />
          <ConditionFieldInput
            v-if="field.$formkit === 'text' || field.$formkit === 'number'"
            :condition="localCondition"
            :field="field"
            :field-error="fieldErrors[field.name]"
            @input="debouncedValidateField"
            @blur="validateField" />
          <MultiSelect
            v-if="field.$formkit === 'multiselect'"
            v-model="localCondition[field.name]"
            :options="typeof field.options === 'function' ? field.options(localCondition) : field.options"
            optionLabel="label"
            optionValue="value"
            display="chip" />
          <IntegrationAutoComplete
            v-if="field.$formkit === 'autocomplete'"
            v-model="localCondition[field.name]"
            :options="field.options"
            :multiple="field.multiple" />
          <DatePicker
            v-if="field.$formkit === 'date'"
            :modelValue="getDate(field)"
            @update:modelValue="updateDate($event, field)"
            :showTime="field.timeOnly"
            :timeOnly="field.timeOnly" />
          <div v-if="field.$formkit === 'content'" class="flex items-center">
            {{ field.content }}
          </div>
        </template>
      </div>
    </div>
    <div class="flex-shrink-0">
      <XMarkIcon
        @click="deleteCondition"
        class="ml-2 mt-3.5 w-4 cursor-pointer" />
    </div>
  </div>
</template>

<script setup>
import { XMarkIcon, ExclamationTriangleIcon } from '@heroicons/vue/24/outline'
import {
  makeSchema,
  makeSourceFields
} from '@/modules/segment/data/logicDefinitionSchema'
import { FilterMatchMode, FilterService } from '@primevue/core/api'

const props = defineProps({
  condition: Object,
  sourceFields: { type: Array, default: null },
  formSchema: { type: Array, default: null }
})

const emit = defineEmits(['update:condition'])

const conditionEl = ref(null)
const localCondition = ref(props.condition)
const fieldErrors = ref({})  // Track validation errors for each field

watch(
  localCondition,
  (newValue) => {
    emit('update:condition', newValue)
  },
  { deep: true }
)

const segmentGroup = inject('segmentGroup', null)

const integrationStore = useIntegrationStore()
const { integratedPartner, partnerData, partners } = storeToRefs(integrationStore)

const deleteCondition = () => {
  emit('delete:condition')
}

const getDate = (field) => {
  if (!localCondition.value[field.name]) return null

  if (field.timeOnly) {
    const time = new Date(localCondition.value[field.name])
    return `${time.getHours()}:${time.getMinutes()}`
  } else {
    return new Date(localCondition.value[field.name]).toLocaleDateString()
  }
}

const updateDate = (date, field) => {
  // if (!field.timeOnly) {
  localCondition.value[field.name] = date
  // } else {
  //   localCondition.value[field.name] = new Date(date).getTime()
  // }
}

const combinedSourceType = computed({
  get: () => {
    if (localCondition.value.$source && localCondition.value.$type) {
      return `${localCondition.value.$source}*${localCondition.value.$type}`
    }
    return null
  },
  set: (newValue) => {
    const [source, type] = newValue.split('*')
    localCondition.value.$source = source
    localCondition.value.$type = type
  }
})

watch(combinedSourceType, (newValue) => {
  // Clear validation errors when changing condition type
  fieldErrors.value = {}

  availableFields.value.forEach((field) => {
    if (field.$formkit === 'select') {
      // Handle both array and function options
      const options =
        typeof field.options === 'function'
          ? field.options(localCondition.value)
          : field.options
      if (options && options.length > 0) {
        localCondition.value[field.name] = options[0].value
      }
    } else if (field.$formkit === 'autocomplete') {
      if (field.multiple) {
        localCondition.value[field.name] = []
      } else {
        localCondition.value[field.name] = undefined
      }
    } else {
      localCondition.value[field.name] = ''
    }
  })
})

// Watch for occurrence changes to set defaults
watch(
  () => localCondition.value.occurrence,
  (newOccurrence) => {
    if (newOccurrence === 'any') {
      // Set default values for the new flexible format
      if (!localCondition.value.occurrenceOperator) {
        localCondition.value.occurrenceOperator = 'gte' // Default to 'at least'
      }
      if (!localCondition.value.occurrenceCount) {
        localCondition.value.occurrenceCount = 1
      }
      if (!localCondition.value.timeframe) {
        localCondition.value.timeframe = 'ever' // Default to 'ever'
      }
    } else if (newOccurrence === 'not_any') {
      // For "Has not visited", set timeframe default
      if (!localCondition.value.timeframe) {
        localCondition.value.timeframe = 'ever' // Default to 'ever'
      }
      // Clear operator and count as they're not used for "not_any"
      delete localCondition.value.occurrenceOperator
      delete localCondition.value.occurrenceCount
    }
  }
)

// Watch for timeframe changes to set duration/unit defaults
watch(
  () => localCondition.value.timeframe,
  (newTimeframe) => {
    if (newTimeframe === 'within' || newTimeframe === 'outside') {
      // Set default duration and unit if not already set
      if (!localCondition.value.duration) {
        localCondition.value.duration = 1
      }
      if (!localCondition.value.unit) {
        localCondition.value.unit = 'week'
      }
    }
  }
)

const sourceLabel = computed(() => {
  return resolvedSourceFields.value.find(
    (field) => field.name === localCondition.value.$source
  )?.group
})

const sourceValue = computed(() => {
  return resolvedSourceFields.value
    .find((field) => field.name === localCondition.value.$source)
    ?.options.find((option) => option.value === combinedSourceType.value)?.label
})

const resolvedSourceFields = computed(() => {
  if (props.sourceFields) {
    return props.sourceFields
  }

  return makeSourceFields({
    partner: integratedPartner.value,
    context: { ignoreSegmentGroups: segmentGroup ? [segmentGroup.value] : [] }
  })
})

// Flag conditions whose $source isn't available under the current partner —
// e.g., stale `$source: 'drip'` rules left behind after a team disconnects
// Drip and moves to Kit. Surfaces the same way field-level validation does
// (red border + tooltip) so the user can see why the condition looks blank
// and click the X to remove it. We deliberately DO NOT auto-delete these in
// a repair() hook — that would silently destroy user-authored rules if the
// integration is only temporarily disconnected (expired OAuth, mid-migration).
const isDisconnectedSource = computed(() => {
  const source = localCondition.value.$source
  if (!source) return false
  return !resolvedSourceFields.value.some((f) => f.name === source)
})

// Resolve the human-readable label for the disconnected source (e.g. "drip" → "Drip")
// by looking it up in the full partners list from the integration store. Falls back
// to the raw internal name if the partner isn't in the list (e.g., deprecated platform).
const disconnectedSourceLabel = computed(() => {
  const source = localCondition.value.$source
  if (!source) return ''
  return partners.value?.find((p) => p.name === source)?.label || source
})

const availableFields = computed(() => {
  return resolvedFormKitSchema.value.filter((field) => {
    if (!field.can) return false
    return typeof field.can === 'function'
      ? field.can(localCondition.value)
      : field.can
  })
})

const resolvedFormKitSchema = computed(() => {
  if (props.formSchema) {
    return props.formSchema
  }

  return makeSchema({ partner: integratedPartner.value })
})

// TODO: Refactor

// const customFields = getIntegrationData('custom_fields', (field) => ({
//   label: field.label,
//   value: String(field.id)
// }))

// const tags = getIntegrationData('tags', (tag) => ({
//   label: tag.label,
//   value: String(tag.id)
// }))

// const lists = getIntegrationData('lists', (list) => ({
//   label: list.label,
//   value: String(list.id)
// }))

const dataMappings = {
  customFields: [],
  tags: [],
  lists: []
}

const dataSearch = (field, event) => {
  const query = event.query
  const data = dataMappings[field.type]
  if (!query) return data.value
  field.suggestions = data.value.filter((item) =>
    item.label.toLowerCase().includes(query.toLowerCase())
  )
}

// const formKitData = computed(() => {
//   return {
//     source: localCondition.value.$source,
//     type: localCondition.value.$type,
//     sourceValue: combinedSourceType.value,
//     condition: localCondition.value,
//     customFields: async () => customFields.value,
//     tags: async () => tags.value,
//     lists: async () => lists.value
//   }
// })

/* Actions */

const validateField = (field, value) => {
  // Only validate if the field has a validate function
  if (field.validate && typeof field.validate === 'function') {
    // Pass both the value and the entire condition object for context-aware validation
    const error = field.validate(value, localCondition.value)
    if (error) {
      fieldErrors.value[field.name] = error
    } else {
      delete fieldErrors.value[field.name]
    }
  }
}

// Debounced validation to prevent focus loss during typing
// Validation runs 300ms after typing stops, but immediate on blur
const debouncedValidateField = useDebounceFn((field, value) => {
  validateField(field, value)
}, 300)

const specialFieldChange = (newValue) => {
  const [source, type] = newValue.split('*')
  const fields = Object.keys(localCondition.value)
  fields.forEach((field) => delete localCondition.value[field])
  localCondition.value.$source = source
  localCondition.value.$type = type
  // Clear any validation errors when changing source/type
  fieldErrors.value = {}
}

// Validate fields on mount. Runs unconditionally (including on blank values)
// because some validators flag empty input as the error — e.g. custom-field
// value inputs with `equals` operator, which the snippet short-circuits to
// false at runtime and thus can never match.
onMounted(() => {
  availableFields.value.forEach((field) => {
    if (field.validate) {
      validateField(field, localCondition.value[field.name])
    }
  })
})

// Revalidate when operator changes
watch(
  () => localCondition.value.operator,
  () => {
    const valueField = availableFields.value.find(field => field.name === 'value')
    if (valueField && valueField.validate) {
      validateField(valueField, localCondition.value.value)
    }
  }
)

// onUpdated(() => {
//   const el = conditionEl.value
//   el.querySelectorAll('input').forEach((input) => {
//     input.style.width = input.value.length + 'ch'
//   })
// })
</script>
